import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Printer } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SCENARIO_EVENT_KINDS, SCENARIO_KINDS, SCENARIO_PRESETS } from '@loot/shared';
import { api } from '../../api.ts';
import { ExportButton } from '../../components/ExportButton.tsx';
import { changed, FormModal, FormSection } from '../../components/FormModal.tsx';
import { ListToolbar } from '../../components/ListFilters.tsx';
import {
  Badge,
  Button,
  EmptyRow,
  ErrorBox,
  Field,
  Input,
  Loading,
  NumCell,
  Panel,
  Select,
  Table,
  Textarea,
} from '../../components/ui.tsx';
import { normalizeDecimal } from '../fpa/expense-form.ts';
import { useFormat } from '../purchasing/common.tsx';
import { BarChart, LineChart } from './TreasuryCharts.tsx';
import { useEntityPicker, type ReportTable } from './TreasuryOverview.tsx';
import { CurrencySelect } from '../../components/forms/CurrencySelect.tsx';

const KEY = ['finance', 'treasury'];
const numberFormat = new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 });
const formatNumber = (v: number) => numberFormat.format(v);

function useAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ path, body, put }: { path: string; body?: unknown; put?: boolean }) =>
      put
        ? api.put<Record<string, unknown>>(`/finance/treasury${path}`, body ?? {})
        : api.post<Record<string, unknown>>(`/finance/treasury${path}`, body ?? {}),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

interface Estimate {
  costCenterId: string;
  expenseTypeId: string;
  classificationId: string;
  month: string;
  currency: string;
  budget: string;
  covered: string;
  uncovered: string;
  date: string;
  status: 'scheduled' | 'without_entry' | 'rescheduled';
}

/**
 * Orçamento do FP&A no caixa (Entrega 6). Sem a leitura do FP&A, a tela diz "conexão pendente" e
 * não mostra estimativa. O detalhe só para quem tem acesso ao FP&A (D-20); os outros veem por dia.
 */
export function TreasuryBudget() {
  const { t } = useTranslation();
  const fmt = useFormat();
  const action = useAction();
  const { entity, picker, state } = useEntityPicker();
  const [decide, setDecide] = useState<Estimate | null>(null);
  const id = entity?.legalEntityId;
  const status = useQuery({
    queryKey: [...KEY, 'budget', 'status', id],
    queryFn: () =>
      api.get<{ connected: boolean; seesDetail: boolean; canDecide: boolean }>(
        `/finance/treasury/budget/status?legalEntityId=${id}`,
      ),
    enabled: !!id,
  });
  const estimates = useQuery({
    queryKey: [...KEY, 'budget', 'estimates', id],
    queryFn: () =>
      api.get<{
        connected: boolean;
        detail: boolean;
        withoutEntry: number;
        daily: { date: string; currency: string; amount: string; count: number }[];
        estimates: Estimate[];
      }>(`/finance/treasury/budget/estimates?legalEntityId=${id}&days=365`),
    enabled: !!id,
  });
  const impacts = useQuery({
    queryKey: [...KEY, 'budget', 'impacts', id],
    queryFn: () =>
      api.get<{
        connected: boolean;
        impacts: {
          type: string;
          id: string;
          description: string;
          date: string;
          currency: string;
          amount: string;
          indicator: string;
          origin: string;
        }[];
      }>(`/finance/treasury/budget/impacts?legalEntityId=${id}`),
    enabled: !!id,
  });
  const comparison = useQuery({
    queryKey: [...KEY, 'budget', 'comparison', id],
    queryFn: () =>
      api.get<ReportTable>(`/finance/treasury/reports/budget_comparison?legalEntityId=${id}`),
    enabled: !!id && !!status.data?.seesDetail && !!status.data?.connected,
  });
  if (state) return state;
  const e = estimates.data;
  return (
    <div className="flex flex-col gap-6">
      <ListToolbar>{picker}</ListToolbar>
      {status.isError && <ErrorBox error={status.error} />}
      {status.data && !status.data.connected && (
        <Panel>
          <p className="text-sm">{t('finance.budget.pending')}</p>
          <p className="text-sm text-muted">{t('finance.budget.pendingHint')}</p>
        </Panel>
      )}
      <ErrorBox error={action.error} />
      {e?.connected && (
        <Panel title={t('finance.budget.uncovered')}>
          {e.withoutEntry > 0 && (
            <p className="text-sm">{t('finance.budget.withoutEntry', { n: e.withoutEntry })}</p>
          )}
          {e.detail ? (
            <Table
              head={[
                t('finance.cash.fields.date'),
                t('finance.budget.month'),
                { label: t('finance.budget.budgetValue'), align: 'right' },
                { label: t('finance.budget.covered'), align: 'right' },
                { label: t('finance.budget.uncoveredValue'), align: 'right' },
                t('finance.cash.fields.situation'),
                '',
              ]}
            >
              {e.estimates.length === 0 && (
                <EmptyRow colSpan={7}>{t('finance.budget.none')}</EmptyRow>
              )}
              {e.estimates.map((x) => (
                <tr
                  key={`${x.costCenterId}|${x.expenseTypeId}|${x.classificationId}|${x.month}|${x.currency}`}
                >
                  <td className="whitespace-nowrap">{fmt.date(x.date)}</td>
                  <td>{x.month.slice(0, 7)}</td>
                  <NumCell>{fmt.money(x.budget, x.currency)}</NumCell>
                  <NumCell>{fmt.money(x.covered, x.currency)}</NumCell>
                  <NumCell>{fmt.money(x.uncovered, x.currency)}</NumCell>
                  <td>
                    <Badge tone={x.status === 'without_entry' ? 'off' : 'info'}>
                      {t(`finance.budget.statuses.${x.status}`)}
                    </Badge>
                  </td>
                  <td className="text-right">
                    {status.data?.canDecide && (
                      <Button variant="secondary" onClick={() => setDecide(x)}>
                        {t('finance.budget.decide')}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </Table>
          ) : (
            <Table
              head={[
                t('finance.cash.fields.date'),
                { label: t('finance.budget.uncoveredValue'), align: 'right' },
              ]}
            >
              {e.daily.length === 0 && <EmptyRow colSpan={2}>{t('finance.budget.none')}</EmptyRow>}
              {e.daily.map((d) => (
                <tr key={`${d.date}|${d.currency}`}>
                  <td>{fmt.date(d.date)}</td>
                  <NumCell>{fmt.money(d.amount, d.currency)}</NumCell>
                </tr>
              ))}
            </Table>
          )}
        </Panel>
      )}
      <Panel title={t('finance.budget.impacts')}>
        <p className="text-sm text-muted">{t('finance.budget.impactsHint')}</p>
        {impacts.isError && <ErrorBox error={impacts.error} />}
        {impacts.data && (
          <Table
            head={[
              t('finance.cash.fields.date'),
              t('finance.cash.fields.description'),
              { label: t('finance.cash.fields.amount'), align: 'right' },
              t('finance.budget.indicator'),
            ]}
          >
            {impacts.data.impacts.length === 0 && (
              <EmptyRow colSpan={4}>{t('finance.budget.noImpacts')}</EmptyRow>
            )}
            {impacts.data.impacts.map((i) => (
              <tr key={`${i.type}:${i.id}`}>
                <td className="whitespace-nowrap">{fmt.date(i.date)}</td>
                <td>
                  {i.description}
                  <div className="text-xs text-muted">{t(`finance.budget.origins.${i.type}`)}</div>
                </td>
                <NumCell>{fmt.money(i.amount, i.currency)}</NumCell>
                <td>
                  <Badge tone={i.indicator === 'outside' ? 'off' : 'neutral'}>
                    {t(`finance.budget.indicators.${i.indicator}`)}
                  </Badge>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Panel>
      {comparison.data && comparison.data.rows.length > 0 && (
        <Panel>
          <BarChart
            title={t('finance.budget.comparison')}
            labels={comparison.data.rows.map(
              (r) => `${String(r.month).slice(0, 7)} ${String(r.nature)}`,
            )}
            format={(v) => formatNumber(v)}
            series={(['budget', 'forecast', 'projected', 'realized'] as const).map((k) => ({
              name: t(`finance.reports.columns.${k}`),
              values: comparison.data.rows.map((r) => Number(r[k] ?? 0)),
            }))}
          />
        </Panel>
      )}
      {decide && id && (
        <DecideForm entityId={id} estimate={decide} onDone={() => setDecide(null)} />
      )}
    </div>
  );
}

function DecideForm({
  entityId,
  estimate,
  onDone,
}: {
  entityId: string;
  estimate: Estimate;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const save = useAction();
  const [start] = useState({ action: 'reschedule', newDate: '', reason: '' });
  const [f, setF] = useState(start);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) =>
    setF((s) => ({ ...s, [k]: e.target.value }));
  return (
    <FormModal
      open
      size="md"
      title={t('finance.budget.decide')}
      dirty={changed(start, f)}
      saving={save.isPending}
      error={save.error}
      onSave={() =>
        save.mutate(
          {
            path: '/budget/estimate-actions',
            body: {
              legalEntityId: entityId,
              costCenterId: estimate.costCenterId,
              expenseTypeId: estimate.expenseTypeId,
              classificationId: estimate.classificationId,
              month: estimate.month,
              action: f.action,
              newDate: f.action === 'reschedule' ? f.newDate : null,
              reason: f.reason,
            },
          },
          { onSuccess: onDone },
        )
      }
      onClose={onDone}
    >
      <FormSection columns={1}>
        <Field label={t('finance.budget.action')} required>
          <Select id="bd-action" value={f.action} onChange={set('action')}>
            {(['reschedule', 'discard'] as const).map((a) => (
              <option key={a} value={a}>
                {t(`finance.budget.actions.${a}`)}
              </option>
            ))}
          </Select>
        </Field>
        {f.action === 'reschedule' && (
          <Field label={t('finance.budget.newDate')} required>
            <Input id="bd-date" type="date" required value={f.newDate} onChange={set('newDate')} />
          </Field>
        )}
        <Field label={t('finance.cash.fields.reason')} required>
          <Textarea
            id="bd-reason"
            required
            maxLength={1000}
            value={f.reason}
            onChange={set('reason')}
          />
        </Field>
      </FormSection>
    </FormModal>
  );
}

interface Scenario {
  id: string;
  name: string;
  kind: (typeof SCENARIO_KINDS)[number];
  variables: Record<string, unknown>;
  horizonDays: number;
  status: 'active' | 'archived';
  notes: string | null;
}

/** Cenários (Entrega 8): salvar, duplicar, comparar e arquivar. Nunca geram lançamentos. */
export function TreasuryScenarios() {
  const { t } = useTranslation();
  const fmt = useFormat();
  const action = useAction();
  const { entity, picker, state } = useEntityPicker();
  const [editing, setEditing] = useState<Scenario | 'new' | null>(null);
  const [chosen, setChosen] = useState<string[]>([]);
  const id = entity?.legalEntityId;
  const list = useQuery({
    queryKey: [...KEY, 'scenarios', id],
    queryFn: () =>
      api.get<{ canManage: boolean; scenarios: Scenario[] }>(
        `/finance/treasury/scenarios?legalEntityId=${id}`,
      ),
    enabled: !!id,
  });
  const run = useQuery({
    queryKey: [...KEY, 'scenarios', 'run', id, chosen.join(',')],
    queryFn: () =>
      api.get<{
        base: { results: Result[]; totals: { currency: string; date: string; closing: string }[] };
        scenarios: {
          id: string;
          name: string;
          results: Result[];
          totals: { currency: string; date: string; closing: string }[];
        }[];
      }>(`/finance/treasury/scenarios/run?legalEntityId=${id}&ids=${chosen.join(',')}`),
    enabled: !!id,
  });
  if (state) return state;
  const canManage = list.data?.canManage ?? false;
  const r = run.data;
  const currencies = r ? [...new Set(r.base.totals.map((x) => x.currency))] : [];
  return (
    <div className="flex flex-col gap-6">
      <ListToolbar
        action={
          canManage && (
            <Button onClick={() => setEditing('new')}>{t('finance.scenarios.new')}</Button>
          )
        }
      >
        {picker}
      </ListToolbar>
      <p className="text-sm text-muted">{t('finance.scenarios.about')}</p>
      <ErrorBox error={action.error} />
      <Panel title={t('finance.scenarios.title')}>
        {list.isPending && <Loading />}
        {list.isError && <ErrorBox error={list.error} />}
        {list.data && (
          <Table
            head={[
              '',
              t('finance.scenarios.name'),
              t('finance.scenarios.kind'),
              t('finance.scenarios.horizon'),
              t('finance.cash.fields.situation'),
              '',
            ]}
          >
            {list.data.scenarios.length === 0 && (
              <EmptyRow colSpan={6}>{t('finance.scenarios.none')}</EmptyRow>
            )}
            {list.data.scenarios.map((s) => (
              <tr key={s.id}>
                <td>
                  {s.status === 'active' && (
                    <input
                      type="checkbox"
                      aria-label={t('finance.scenarios.compare')}
                      checked={chosen.includes(s.id)}
                      onChange={(e) =>
                        setChosen((c) =>
                          e.target.checked ? [...c, s.id].slice(-4) : c.filter((x) => x !== s.id),
                        )
                      }
                    />
                  )}
                </td>
                <td>{s.name}</td>
                <td>{t(`finance.scenarios.kinds.${s.kind}`)}</td>
                <td>{t('finance.scenarios.days', { n: s.horizonDays })}</td>
                <td>
                  <Badge tone={s.status === 'active' ? 'ok' : 'neutral'}>
                    {t(`finance.scenarios.statuses.${s.status}`)}
                  </Badge>
                </td>
                <td className="text-right">
                  {canManage && s.status === 'active' && (
                    <div className="flex justify-end gap-2">
                      <Button variant="secondary" onClick={() => setEditing(s)}>
                        {t('finance.scenarios.edit')}
                      </Button>
                      <Button
                        variant="secondary"
                        disabled={action.isPending}
                        onClick={() => action.mutate({ path: `/scenarios/${s.id}/duplicate` })}
                      >
                        {t('finance.scenarios.duplicate')}
                      </Button>
                      <Button
                        variant="secondary"
                        disabled={action.isPending}
                        onClick={() => action.mutate({ path: `/scenarios/${s.id}/archive` })}
                      >
                        {t('finance.scenarios.archive')}
                      </Button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Panel>
      {run.isError && <ErrorBox error={run.error} />}
      {r &&
        currencies.map((c) => {
          const base = r.base.totals.filter((x) => x.currency === c);
          const rows = [
            {
              name: t('finance.scenarios.kinds.base'),
              result: r.base.results.find((x) => x.currency === c),
            },
            ...r.scenarios.map((s) => ({
              name: s.name,
              result: s.results.find((x) => x.currency === c),
            })),
          ];
          return (
            <Panel key={c} title={t('finance.scenarios.results', { currency: c })}>
              <Table
                head={[
                  t('finance.scenarios.name'),
                  { label: t('finance.reports.columns.lowest'), align: 'right' },
                  t('finance.reports.columns.lowestDate'),
                  t('finance.reports.columns.firstNegative'),
                  { label: t('finance.reports.columns.fundingNeed'), align: 'right' },
                  { label: t('finance.reports.columns.vsBase'), align: 'right' },
                ]}
              >
                {rows.map((x) => (
                  <tr key={x.name}>
                    <td>{x.name}</td>
                    <NumCell>{x.result ? fmt.money(x.result.lowest.closing, c) : '—'}</NumCell>
                    <td>{x.result ? fmt.date(x.result.lowest.date) : '—'}</td>
                    <td>{x.result?.firstNegative ? fmt.date(x.result.firstNegative) : '—'}</td>
                    <NumCell>{x.result ? fmt.money(x.result.maxFundingNeed, c) : '—'}</NumCell>
                    <NumCell>
                      {x.result?.lowestVsBase ? fmt.money(x.result.lowestVsBase, c) : '—'}
                    </NumCell>
                  </tr>
                ))}
              </Table>
              <LineChart
                title={t('finance.scenarios.chart', { currency: c })}
                labels={base.map((x) => fmt.date(x.date))}
                format={(v) => fmt.money(v.toFixed(2), c)}
                series={[
                  {
                    name: t('finance.scenarios.kinds.base'),
                    values: base.map((x) => Number(x.closing)),
                  },
                  ...r.scenarios.slice(0, 3).map((s) => {
                    const by = new Map(
                      s.totals.filter((x) => x.currency === c).map((x) => [x.date, x.closing]),
                    );
                    return {
                      name: s.name,
                      values: base.map((x) => (by.has(x.date) ? Number(by.get(x.date)) : null)),
                    };
                  }),
                ]}
              />
            </Panel>
          );
        })}
      {editing && id && (
        <ScenarioForm
          entityId={id}
          scenario={editing === 'new' ? null : editing}
          onDone={() => setEditing(null)}
        />
      )}
    </div>
  );
}

interface Result {
  currency: string;
  lowest: { date: string; closing: string };
  firstNegative: string | null;
  maxFundingNeed: string;
  lowestVsBase: string | null;
}

interface EventRow {
  kind: (typeof SCENARIO_EVENT_KINDS)[number];
  label: string;
  direction: 'in' | 'out';
  date: string;
  amount: string;
  currency: string;
  months: string;
}

function ScenarioForm({
  entityId,
  scenario,
  onDone,
}: {
  entityId: string;
  scenario: Scenario | null;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const save = useAction();
  const v = (scenario?.variables ?? {}) as Record<string, unknown>;
  const [start] = useState(() => ({
    name: scenario?.name ?? '',
    kind: scenario?.kind ?? 'custom',
    horizonDays: String(scenario?.horizonDays ?? 90),
    receiptsReductionPct: String(v.receiptsReductionPct ?? 0),
    expenseGrowthPct: String(v.expenseGrowthPct ?? 0),
    receiptDelayDays: String(v.receiptDelayDays ?? 0),
    paymentShiftDays: String(v.paymentShiftDays ?? 0),
    fxShockPct: String(v.fxShockPct ?? 0),
    notes: scenario?.notes ?? '',
  }));
  const [f, setF] = useState(start);
  const [events, setEvents] = useState<EventRow[]>(() =>
    ((v.events as (Omit<EventRow, 'months'> & { months: number })[] | undefined) ?? []).map(
      (e) => ({
        ...e,
        months: String(e.months),
      }),
    ),
  );
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) =>
    setF((s) => ({ ...s, [k]: e.target.value }));
  const preset = (kind: string) => {
    const p = SCENARIO_PRESETS[kind as keyof typeof SCENARIO_PRESETS];
    if (!p) return;
    setF((s) => ({
      ...s,
      kind: kind as Scenario['kind'],
      receiptsReductionPct: String(p.receiptsReductionPct ?? 0),
      expenseGrowthPct: String(p.expenseGrowthPct ?? 0),
      receiptDelayDays: String(p.receiptDelayDays ?? 0),
    }));
  };
  const n = (s: string) => Number(s.replace(',', '.')) || 0;
  const setEvent = (i: number, k: keyof EventRow, value: string) =>
    setEvents((list) => list.map((e, j) => (j === i ? { ...e, [k]: value } : e)));
  return (
    <FormModal
      open
      title={scenario ? t('finance.scenarios.edit') : t('finance.scenarios.new')}
      dirty={changed(start, f) || events.length > 0}
      saving={save.isPending}
      error={save.error}
      onSave={() =>
        save.mutate(
          {
            path: scenario ? `/scenarios/${scenario.id}` : '/scenarios',
            put: !!scenario,
            body: {
              legalEntityId: entityId,
              name: f.name,
              kind: f.kind,
              horizonDays: n(f.horizonDays),
              notes: f.notes,
              variables: {
                receiptsReductionPct: n(f.receiptsReductionPct),
                expenseGrowthPct: n(f.expenseGrowthPct),
                receiptDelayDays: Math.round(n(f.receiptDelayDays)),
                paymentShiftDays: Math.round(n(f.paymentShiftDays)),
                fxShockPct: n(f.fxShockPct),
                events: events.map((e) => ({
                  ...e,
                  amount: normalizeDecimal(e.amount),
                  months: Math.round(n(e.months)) || 1,
                })),
              },
            },
          },
          { onSuccess: onDone },
        )
      }
      onClose={onDone}
    >
      <FormSection>
        <Field label={t('finance.scenarios.name')} required>
          <Input id="sc-name" required maxLength={120} value={f.name} onChange={set('name')} />
        </Field>
        <Field label={t('finance.scenarios.kind')} required>
          <Select
            id="sc-kind"
            value={f.kind}
            onChange={(e) => preset(e.target.value) ?? set('kind')(e)}
          >
            {SCENARIO_KINDS.map((k) => (
              <option key={k} value={k}>
                {t(`finance.scenarios.kinds.${k}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('finance.scenarios.horizon')} required>
          <Input
            id="sc-horizon"
            inputMode="numeric"
            value={f.horizonDays}
            onChange={set('horizonDays')}
          />
        </Field>
        {(
          [
            'receiptsReductionPct',
            'expenseGrowthPct',
            'receiptDelayDays',
            'paymentShiftDays',
            'fxShockPct',
          ] as const
        ).map((k) => (
          <Field key={k} label={t(`finance.scenarios.variables.${k}`)}>
            <Input id={`sc-${k}`} inputMode="decimal" value={f[k]} onChange={set(k)} />
          </Field>
        ))}
      </FormSection>
      <FormSection title={t('finance.scenarios.events')}>
        {events.map((e, i) => (
          <div key={i} className="flex flex-col gap-2 rounded border border-rule p-2 sm:col-span-2">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <Select
                aria-label={t('finance.scenarios.eventKind')}
                value={e.kind}
                onChange={(ev) => setEvent(i, 'kind', ev.target.value)}
              >
                {SCENARIO_EVENT_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {t(`finance.scenarios.eventKinds.${k}`)}
                  </option>
                ))}
              </Select>
              <Input
                aria-label={t('finance.scenarios.eventLabel')}
                value={e.label}
                onChange={(ev) => setEvent(i, 'label', ev.target.value)}
              />
              <Select
                aria-label={t('finance.forecasts.fields.direction')}
                value={e.direction}
                onChange={(ev) => setEvent(i, 'direction', ev.target.value)}
              >
                {(['out', 'in'] as const).map((d) => (
                  <option key={d} value={d}>
                    {t(`finance.forecasts.directions.${d}`)}
                  </option>
                ))}
              </Select>
              <Input
                aria-label={t('finance.cash.fields.date')}
                type="date"
                value={e.date}
                onChange={(ev) => setEvent(i, 'date', ev.target.value)}
              />
              <Input
                aria-label={t('finance.cash.fields.amount')}
                inputMode="decimal"
                value={e.amount}
                onChange={(ev) => setEvent(i, 'amount', ev.target.value)}
              />
              <CurrencySelect
                compact
                aria-label={t('finance.forecasts.fields.currency')}
                value={e.currency}
                onChange={(code) => setEvent(i, 'currency', code)}
              />
              <Input
                aria-label={t('finance.scenarios.months')}
                inputMode="numeric"
                value={e.months}
                onChange={(ev) => setEvent(i, 'months', ev.target.value)}
              />
              <Button
                variant="secondary"
                onClick={() => setEvents((l) => l.filter((_, j) => j !== i))}
              >
                {t('finance.scenarios.removeEvent')}
              </Button>
            </div>
          </div>
        ))}
        <div>
          <Button
            variant="secondary"
            onClick={() =>
              setEvents((l) => [
                ...l,
                {
                  kind: 'extraordinary_expense',
                  label: '',
                  direction: 'out',
                  date: '',
                  amount: '',
                  currency: 'BRL',
                  months: '1',
                },
              ])
            }
          >
            {t('finance.scenarios.addEvent')}
          </Button>
        </div>
      </FormSection>
      <FormSection columns={1}>
        <Field label={t('finance.receipts.fields.notes')}>
          <Textarea id="sc-notes" maxLength={2000} value={f.notes} onChange={set('notes')} />
        </Field>
      </FormSection>
    </FormModal>
  );
}

const REPORTS = [
  'daily',
  'monthly',
  'by_entity',
  'by_account',
  'consolidated',
  'inflows',
  'outflows',
  'outside_budget',
  'budget_comparison',
  'liquidity',
  'divergences',
  'accuracy',
  'scenarios',
  'transfers',
] as const;

/**
 * Relatórios (§16): os 14, com os mesmos valores na tela, no Excel e na impressão (PDF pela
 * impressão do navegador, D-11). Filtros de período, horizonte e moeda (consolidado).
 */
export function TreasuryReports() {
  const { t } = useTranslation();
  const fmt = useFormat();
  const { entity, picker, state } = useEntityPicker();
  const [report, setReport] = useState<(typeof REPORTS)[number]>('daily');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [days, setDays] = useState('90');
  const [currency, setCurrency] = useState('BRL');
  const filters: Record<string, string> = {
    legalEntityId: entity?.legalEntityId ?? '',
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
    days,
    currency,
  };
  const qs = new URLSearchParams(filters).toString();
  const data = useQuery({
    queryKey: [...KEY, 'report', report, qs],
    queryFn: () => api.get<ReportTable>(`/finance/treasury/reports/${report}?${qs}`),
    enabled: !!entity,
  });
  if (state) return state;
  const cell = (v: unknown, col: string, row: Record<string, unknown>) => {
    if (v === null || v === undefined) return '—';
    if (typeof v === 'boolean') return v ? t('finance.reports.yes') : t('finance.reports.no');
    if (typeof v === 'number') return formatNumber(v);
    const s = String(v);
    if (col === 'month') return fmt.month(s);
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return fmt.date(s);
    if (col === 'flags') {
      return (s === '' ? ['ok'] : s.split(', '))
        .map((f) => t(`finance.flow.flags.${f}`))
        .join(', ');
    }
    if (col === 'origin') return t(`finance.budget.origins.${s.split('.').pop()}`);
    if (
      col === 'kind' ||
      col === 'status' ||
      col === 'indicator' ||
      col === 'nature' ||
      col === 'eliminated'
    ) {
      return t(`finance.reports.values.${s}`, { defaultValue: s });
    }
    // Valor com quatro casas, com a moeda da linha ou no próprio texto ("3000.0000 BRL").
    const money = col === 'errorPct' ? null : s.match(/^(-?\d+\.\d{4})(?: ([A-Z]{3}))?$/);
    if (money) {
      const currency = money[2] ?? (typeof row.currency === 'string' ? row.currency : null);
      return currency ? fmt.money(money[1]!, currency) : formatNumber(Number(money[1]));
    }
    return s;
  };
  const numeric = (col: string) =>
    ![
      'date',
      'month',
      'currency',
      'flags',
      'entity',
      'account',
      'kind',
      'status',
      'resolution',
      'description',
      'origin',
      'indicator',
      'scenario',
      'nature',
      'asOf',
      'target',
      'lowestDate',
      'firstNegative',
      'fromEntity',
      'fromAccount',
      'toEntity',
      'toAccount',
      'fromAmount',
      'toAmount',
      'eliminated',
      'partial',
    ].includes(col);
  return (
    <div className="flex flex-col gap-6">
      <ListToolbar
        action={
          data.data && (
            <div className="flex flex-wrap gap-2 print:hidden">
              <ExportButton
                path={`/finance/treasury/reports/${report}/export`}
                filters={filters}
                columns={data.data.columns.map((key) => ({
                  key,
                  label: t(`finance.reports.columns.${key}`),
                }))}
              />
              <Button variant="secondary" onClick={() => window.print()}>
                <Printer size={16} aria-hidden /> {t('finance.reports.print')}
              </Button>
            </div>
          )
        }
      >
        {picker}
        <Select
          id="report-kind"
          aria-label={t('finance.reports.report')}
          value={report}
          onChange={(e) => setReport(e.target.value as (typeof REPORTS)[number])}
        >
          {REPORTS.map((r) => (
            <option key={r} value={r}>
              {t(`finance.reports.names.${r}`)}
            </option>
          ))}
        </Select>
        <Input
          id="report-from"
          type="date"
          aria-label={t('finance.cash.fields.from')}
          value={from}
          onChange={(e) => setFrom(e.target.value)}
        />
        <Input
          id="report-to"
          type="date"
          aria-label={t('finance.cash.fields.to')}
          value={to}
          onChange={(e) => setTo(e.target.value)}
        />
        <Select
          id="report-days"
          aria-label={t('finance.reports.horizon')}
          value={days}
          onChange={(e) => setDays(e.target.value)}
        >
          {['30', '60', '90', '180', '365'].map((d) => (
            <option key={d} value={d}>
              {t('finance.scenarios.days', { n: Number(d) })}
            </option>
          ))}
        </Select>
        {report === 'consolidated' && (
          <Input
            id="report-currency"
            aria-label={t('finance.flow.fields.currency')}
            maxLength={3}
            value={currency}
            onChange={(e) => setCurrency(e.target.value.toUpperCase())}
          />
        )}
      </ListToolbar>
      {data.isPending && <Loading />}
      {data.isError && <ErrorBox error={data.error} />}
      {data.data && (
        <Panel title={t(`finance.reports.names.${report}`)}>
          {data.data.notes.map((n) => (
            <p key={n} className="text-sm text-muted">
              {n.startsWith('missing_rate:')
                ? t('finance.reports.missingRate', { currencies: n.slice(13) })
                : t(`finance.reports.notes.${n}`)}
            </p>
          ))}
          <div className="overflow-x-auto">
            <Table
              head={data.data.columns.map((c) =>
                numeric(c)
                  ? { label: t(`finance.reports.columns.${c}`), align: 'right' as const }
                  : t(`finance.reports.columns.${c}`),
              )}
            >
              {data.data.rows.length === 0 && (
                <EmptyRow colSpan={data.data.columns.length}>{t('finance.reports.empty')}</EmptyRow>
              )}
              {data.data.rows.map((row, i) => (
                <tr key={i}>
                  {data.data.columns.map((c) =>
                    numeric(c) ? (
                      <NumCell key={c}>{cell(row[c], c, row)}</NumCell>
                    ) : (
                      <td key={c} className="text-sm">
                        {cell(row[c], c, row)}
                      </td>
                    ),
                  )}
                </tr>
              ))}
            </Table>
          </div>
        </Panel>
      )}
    </div>
  );
}
